import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tr } from '../../../i18n/i18n';
import i18n from '../../../i18n/i18n';
import {
  IAdminOrganizationListResponse,
  IAdminOrganizationState,
  IAdminOrganizationActionResponse,
  IAdminOrganizationResponse,
  IOrganizationStatusResponse,
  IOrganizationDocumentsResponse,
  IOrganizationInvoicesResponse,
} from '../../../interface/classveew-admin/organization.interface';
import {
  fetchAdminOrganizations,
  createAdminOrganization,
  approveAdminOrganization,
  rejectAdminOrganization,
  revokeAdminOrganization,
  getAdminOrganizationById,
  updateAdminOrganization,
  updateOrganizationStatus,
  fetchOrganizationDocuments,
  fetchOrganizationInvoices,
  markOrganizationInvoiceAsPaid,
} from '../../action/classveew-admin/organization.action';

const initialState: IAdminOrganizationState = {
  organizations: [],
  organization: null,
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
  isLoading: false,
  error: null,
  documents: [],
  documentsPage: 1,
  documentsPageSize: 10,
  documentsTotal: 0,
  documentsTotalPages: 0,
  isLoadingDocuments: false,
  documentsError: null,
  invoices: [],
  invoicesPage: 1,
  invoicesPageSize: 10,
  invoicesTotal: 0,
  invoicesTotalPages: 0,
  isLoadingInvoices: false,
  invoicesError: null,
  isMarkingInvoicePaid: false,
};

const adminOrganizationSlice = createSlice({
  name: 'adminOrganization',
  initialState,
  reducers: {
    resetAdminOrganization: (state) => {
      state.organization = null;
    },
    clearAdminOrganizations: (state) => {
      state.organizations = [];
      state.page = 1;
      state.total = 0;
      state.totalPages = 0;
    },
    clearOrganizationDocuments: (state) => {
      state.documents = [];
      state.documentsPage = 1;
      state.documentsTotal = 0;
      state.documentsTotalPages = 0;
      state.documentsError = null;
    },
    clearOrganizationInvoices: (state) => {
      state.invoices = [];
      state.invoicesPage = 1;
      state.invoicesTotal = 0;
      state.invoicesTotalPages = 0;
      state.invoicesError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all organizations
      .addCase(fetchAdminOrganizations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.organizations = [];
      })
      .addCase(
        fetchAdminOrganizations.fulfilled,
        (state, action: PayloadAction<IAdminOrganizationListResponse>) => {
          state.isLoading = false;
          state.organizations = action.payload.data?.organizations || [];
          state.page = action.payload.data?.page || 1;
          state.pageSize = action.payload.data?.pageSize || 20;
          state.total = action.payload.data?.total || 0;
          state.totalPages = action.payload.data?.totalPages || 0;
          state.error = null;
        },
      )
      .addCase(fetchAdminOrganizations.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_fetch_admin_organizations',
            'Failed to fetch admin organizations.',
          );
        state.organizations = [];
        state.total = 0;
        state.totalPages = 0;
      })
      // Create organization
      .addCase(createAdminOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        createAdminOrganization.fulfilled,
        (state, _action: PayloadAction<IAdminOrganizationResponse>) => {
          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(createAdminOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          tr('g.failed_to_create_organization', 'Failed to create organization.');
      })
      // Approve organization
      .addCase(approveAdminOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        approveAdminOrganization.fulfilled,
        (state, action: PayloadAction<IAdminOrganizationActionResponse>) => {
          state.isLoading = false;
          // Update the organization in the list
          const index = state.organizations.findIndex((org) => org.id === action.payload.data.id);
          if (index !== -1) {
            state.organizations[index] = action.payload.data;
          }
          state.error = null;
        },
      )
      .addCase(approveAdminOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_approve_organization', 'Failed to approve organization.');
      })
      // Reject organization
      .addCase(rejectAdminOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        rejectAdminOrganization.fulfilled,
        (state, action: PayloadAction<IAdminOrganizationActionResponse>) => {
          state.isLoading = false;
          const index = state.organizations.findIndex((org) => org.id === action.payload.data.id);
          if (index !== -1) {
            state.organizations[index] = action.payload.data;
          }
          state.error = null;
        },
      )
      .addCase(rejectAdminOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_reject_organization', 'Failed to reject organization.');
      })
      // Revoke organization
      .addCase(revokeAdminOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        revokeAdminOrganization.fulfilled,
        (state, action: PayloadAction<IAdminOrganizationActionResponse>) => {
          state.isLoading = false;
          const index = state.organizations.findIndex((org) => org.id === action.payload.data.id);
          if (index !== -1) {
            state.organizations[index] = action.payload.data;
          }
          state.error = null;
        },
      )
      .addCase(revokeAdminOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_revoke_organization', 'Failed to revoke organization.');
      })
      // Get organization by ID
      .addCase(getAdminOrganizationById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.organization = null;
      })
      .addCase(
        getAdminOrganizationById.fulfilled,
        (state, action: PayloadAction<IAdminOrganizationResponse>) => {
          state.isLoading = false;
          state.organization = action.payload.data.organization;
          state.error = null;
        },
      )
      .addCase(getAdminOrganizationById.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_get_organization', 'Failed to get organization.');
        state.organization = null;
      })
      // Update organization
      .addCase(updateAdminOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateAdminOrganization.fulfilled,
        (state, action: PayloadAction<IAdminOrganizationResponse>) => {
          state.isLoading = false;
          state.organization = action.payload.data.organization;
          // Update in list if present
          const index = state.organizations.findIndex(
            (org) => org.id === action.payload.data.organization.id,
          );
          if (index !== -1) {
            state.organizations[index] = action.payload.data.organization;
          }
          state.error = null;
        },
      )
      .addCase(updateAdminOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          tr('g.failed_to_update_organization', 'Failed to update organization.');
      })
      // Update organization status
      .addCase(updateOrganizationStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateOrganizationStatus.fulfilled,
        (state, action: PayloadAction<IOrganizationStatusResponse>) => {
          state.isLoading = false;
          // Update the organization status in the list
          const index = state.organizations.findIndex(
            (org) => org.id === action.payload.data.organization.id,
          );
          if (index !== -1) {
            state.organizations[index].status = action.payload.data.organization.status;
          }
          // Update in single organization if loaded
          if (state.organization?.id === action.payload.data.organization.id) {
            state.organization.status = action.payload.data.organization.status;
          }
          state.error = null;
        },
      )
      .addCase(updateOrganizationStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_update_organization_status',
            'Failed to update organization status.',
          );
      })
      // Fetch organization documents
      .addCase(fetchOrganizationDocuments.pending, (state) => {
        state.isLoadingDocuments = true;
        state.documentsError = null;
      })
      .addCase(
        fetchOrganizationDocuments.fulfilled,
        (state, action: PayloadAction<IOrganizationDocumentsResponse>) => {
          state.isLoadingDocuments = false;
          state.documents = action.payload.data?.documents || [];
          state.documentsPage = action.payload.data?.page || 1;
          state.documentsPageSize = action.payload.data?.pageSize || 10;
          state.documentsTotal = action.payload.data?.total || 0;
          state.documentsTotalPages = action.payload.data?.totalPages || 0;
          state.documentsError = null;
        },
      )
      .addCase(fetchOrganizationDocuments.rejected, (state, action) => {
        state.isLoadingDocuments = false;
        state.documentsError =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_fetch_organization_documents',
            'Failed to fetch organization documents.',
          );
        state.documents = [];
      })
      // Fetch organization invoices
      .addCase(fetchOrganizationInvoices.pending, (state) => {
        state.isLoadingInvoices = true;
        state.invoicesError = null;
      })
      .addCase(
        fetchOrganizationInvoices.fulfilled,
        (state, action: PayloadAction<IOrganizationInvoicesResponse>) => {
          state.isLoadingInvoices = false;
          state.invoices = action.payload.data?.invoices || [];
          state.invoicesPage = action.payload.data?.page || 1;
          state.invoicesPageSize = action.payload.data?.pageSize || 10;
          state.invoicesTotal = action.payload.data?.total || 0;
          state.invoicesTotalPages = action.payload.data?.totalPages || 0;
          state.invoicesError = null;
        },
      )
      .addCase(fetchOrganizationInvoices.rejected, (state, action) => {
        state.isLoadingInvoices = false;
        state.invoicesError =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_fetch_organization_invoices',
            'Failed to fetch organization invoices.',
          );
        state.invoices = [];
      })
      .addCase(markOrganizationInvoiceAsPaid.pending, (state) => {
        state.isMarkingInvoicePaid = true;
        state.invoicesError = null;
      })
      .addCase(markOrganizationInvoiceAsPaid.fulfilled, (state) => {
        state.isMarkingInvoicePaid = false;
        state.invoicesError = null;
      })
      .addCase(markOrganizationInvoiceAsPaid.rejected, (state, action) => {
        state.isMarkingInvoicePaid = false;
        state.invoicesError =
          action.payload?.message ||
          i18n.t('toast.failed_to_mark_invoice_as_paid', 'Failed to mark invoice as paid.');
      });
  },
});

export const {
  resetAdminOrganization,
  clearAdminOrganizations,
  clearOrganizationDocuments,
  clearOrganizationInvoices,
} = adminOrganizationSlice.actions;

export default adminOrganizationSlice.reducer;
